/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable tailwindcss/enforces-negative-arbitrary-values */
/**
 * WrappedText – pure CSS truncation pattern: show first 2 lines of text + a bottom band for a button.
 *
 * CSS mechanics only (no js, ssr friendly):
 * 1. Two layers:
 *    - Top: absolute + line-clamp-N → renders exactly N lines (configurable via `clamp` prop),
 *      removed from normal flow.
 *    - Bottom: full text + button; its top part is visually hidden (masked) so only a narrow
 *      tail area with the button remains.
 *
 * 2. Offsets:
 *    - Top layer is pushed down (top:[1lh]); bottom layer is pulled up (-mt-[1lh]). The overlap
 *      hides the first lines of the bottom layer beneath the clamped top layer.
 *
 * 3. Masking bottom layer:
 *    - max-h-[4lh] defines a 4‑line working area.
 *    - clip-path: inset(3lh 0 0 0) cuts away the first 3 lines, leaving only the 4th line band
 *      visible (where the button sits).
 *
 * 4. Flex:
 *    - flex + flex-col + justify-end pin the button to the bottom of the visible band even if
 *      content is short; items-start keeps left alignment.
 *
 * 5. line-clamp + overflow:
 *    - line-clamp-2 limits the top layer; overflow-hidden prevents spillover.
 *
 * 6. Result:
 *    - Visually: two lines of text + an immediate "show more" band, achieved via absolute positioning,
 *      negative margin, lh units, max-height, and clip-path—no measuring JS needed for truncation.
 */

import classNames from 'classnames';
import { PropsWithChildren, useState } from 'react';
import { truncateToBudget } from '../helpers';

const lineClampClass: Record<number, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
};

type WrappedTextProps = {
  showMoreButtonVisible?: boolean;
  containerClassName?: string;
  showMoreButtonText: string;
  clamp?: 1 | 2 | 3;
  /**
   * Opt-in (CMS-1900): character-budget truncation instead of the default CSS
   * line-clamp. When set, the collapsed teaser is computed from
   * `collapsedSourceText` cut to this budget (word-boundary for space-delimited
   * languages, char-boundary otherwise) + ellipsis + the inline toggle on one line.
   * Existing callers (hero blocks, article header) omit this and keep line-clamp.
   */
  characterBudget?: number;
  wordBoundary?: boolean;
  /** Plain-text source for the collapsed teaser; required for budget mode. */
  collapsedSourceText?: string;
};

export const WrappedText: React.FC<PropsWithChildren<WrappedTextProps>> = ({
  children,
  containerClassName,
  showMoreButtonVisible,
  showMoreButtonText,
  clamp = 2,
  characterBudget,
  wordBoundary = true,
  collapsedSourceText,
}) => {
  // if showMoreButton is not visible, the text is open
  const [isOpen, setIsOpen] = useState(!showMoreButtonVisible);
  const textShouldBeClamped = showMoreButtonVisible && !isOpen;

  // Budget mode (CMS-1900): truncate at a per-locale character budget on a whole
  // word/character boundary, keeping the full copy in the DOM for SEO.
  //
  // We do not reuse ExpandableText here: it drives truncation off
  // `window.innerWidth`, which reads differently on the server and the client and
  // would be a hydration mismatch. Character-budget truncation is deterministic and
  // SSR-safe, so the full copy is present in the initial HTML for crawlers.
  if (characterBudget && characterBudget > 0 && collapsedSourceText != null) {
    const { text: teaser, isTruncated } = truncateToBudget(
      collapsedSourceText,
      characterBudget,
      { wordBoundary },
    );
    // Copy already within budget → show it in full, no toggle.
    const collapsed = showMoreButtonVisible && isTruncated && !isOpen;

    return (
      <div className={classNames('relative', containerClassName)}>
        {collapsed ? (
          <>
            {/* Visible teaser + inline toggle, tuned to a single line. The teaser
                is a truncated copy for sighted users only; it is hidden from the
                accessibility tree so screen readers announce the full copy (the
                sr-only span below) once, not the teaser followed by the full copy. */}
            <span aria-hidden="true">{teaser} </span>
            <button
              type="button"
              className="inline whitespace-nowrap"
              aria-expanded={false}
              onClick={() => setIsOpen(true)}
            >
              <u>{showMoreButtonText}</u>
            </button>
            {/* Full copy kept in the server-rendered HTML for crawlers/SEO and read
                by screen readers, visually hidden (not removed from the DOM) while
                collapsed. */}
            <span className="sr-only">{children}</span>
          </>
        ) : (
          children
        )}
      </div>
    );
  }

  return (
    <div className={classNames('relative', containerClassName)}>
      <div
        className={classNames({
          [`${lineClampClass[clamp]} absolute top-[1lh] overflow-hidden`]:
            textShouldBeClamped,
        })}
      >
        {children}
      </div>
      {textShouldBeClamped && (
        <div
          className="-mt-[1lh] flex flex-col items-start justify-end overflow-hidden"
          style={{
            clipPath: `inset(${clamp + 1}lh 0 0 0)`,
            maxHeight: `${clamp + 2}lh`,
          }}
        >
          <div aria-hidden>{children}</div>
          <button type="button" onClick={() => setIsOpen(true)}>
            <u>{showMoreButtonText}</u>
          </button>
        </div>
      )}
    </div>
  );
};
